import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import PhotoUpload from './PhotoUpload';

const API = `${window.location.protocol}//${window.location.hostname}/api`;
function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  }).then(r => r.json());
}

const FMP_TYPE_LABELS = { rack_view: 'Rack View', cassette: 'Cassette Layout', routing: 'Routing Diagram', splicing: 'Splicing Diagram' };

export default function DesignQueue() {
  const user = useUser();
  const userRole = user?.role;
  const [tab, setTab] = useState(['field_engineer'].includes(userRole) ? 'fe' : 'design');
  const [designQueue, setDesignQueue]   = useState([]);
  const [feQueue, setFeQueue]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [publishing, setPublishing]     = useState(null);
  const [verifying, setVerifying]       = useState(null);
  const navigate = useNavigate();

  const isFE = ['field_engineer','admin'].includes(user?.role);
  const isDesign = ['design','admin'].includes(user?.role);

  const load = () => {
    Promise.all([
      api('/fmps/design-queue'),
      api('/fmps/fe-queue'),
    ]).then(([dq, fq]) => {
      setDesignQueue(Array.isArray(dq) ? dq : []);
      setFeQueue(Array.isArray(fq) ? fq : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const publish = async (fmpId) => {
    setPublishing(fmpId);
    await api(`/fmps/${fmpId}/publish`, { method: 'PATCH' });
    load();
    setPublishing(null);
  };

  const acknowledge = async (fmpId) => {
    await api(`/fmps/${fmpId}/acknowledge`, { method: 'PATCH' });
    load();
  };

  const verify = async (fmpId) => {
    setVerifying(fmpId);
    await api(`/fmps/${fmpId}/verify`, { method: 'PATCH' });
    load();
    setVerifying(null);
  };

  if (loading) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0f172a', color: '#64748b', fontFamily: 'sans-serif' }}>Loading...</div>;

  const tabs = [
    { key: 'fe',     label: `FE Verification (${feQueue.length})`,   show: isFE },
    { key: 'design', label: `Design Queue (${designQueue.length})`,   show: isDesign },
  ].filter(t => t.show);

  return (
    <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#0f172a', fontFamily: 'sans-serif', padding: 20 }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>

        <div style={{ marginBottom: 20 }}>
          <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 20, marginBottom: 4 }}>FMP Queue</div>
          <div style={{ color: '#475569', fontSize: 13 }}>Field markup prints pending verification and publishing</div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #1e293b', marginBottom: 20 }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding: '10px 20px', border: 'none', borderBottom: tab === t.key ? '2px solid #3b82f6' : '2px solid transparent', backgroundColor: 'transparent', color: tab === t.key ? '#93c5fd' : '#64748b', cursor: 'pointer', fontSize: 13, fontWeight: tab === t.key ? 'bold' : 'normal' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── FE Verification Tab ── */}
        {tab === 'fe' && (
          <div>
            {feQueue.length === 0 ? (
              <div style={{ backgroundColor: '#1e293b', borderRadius: 12, padding: 48, textAlign: 'center', border: '1px solid #334155' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
                <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 18, marginBottom: 6 }}>No FMPs pending verification</div>
                <div style={{ color: '#64748b', fontSize: 14 }}>All draft FMPs have been reviewed.</div>
              </div>
            ) : feQueue.map(fmp => (
              <div key={fmp.id} style={{ backgroundColor: '#1e293b', borderRadius: 10, padding: 18, marginBottom: 12, border: '1px solid #7c3aed44', borderLeft: '4px solid #7c3aed' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 15, marginBottom: 4 }}>
                      {fmp.work_item_title || `Rack Update — ${fmp.rack_label || fmp.location_name || 'Standalone FMP'}`}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, color: '#7c3aed', backgroundColor: '#7c3aed22', padding: '2px 7px', borderRadius: 4 }}>{FMP_TYPE_LABELS[fmp.fmp_type] || fmp.fmp_type}</span>
                      {!fmp.work_item_id && <span style={{ fontSize: 11, color: '#f59e0b', backgroundColor: '#f59e0b22', padding: '2px 7px', borderRadius: 4 }}>Standalone — No Ticket</span>}
                      {fmp.no_change && <span style={{ fontSize: 11, color: '#94a3b8', backgroundColor: '#33415522', padding: '2px 7px', borderRadius: 4 }}>No Changes</span>}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', textAlign: 'right', flexShrink: 0 }}>
                    <div>Generated: {new Date(fmp.generated_at).toLocaleDateString()}</div>
                    <div>By: {fmp.generated_by_name}</div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
                  {[
                    ['Site', fmp.location_name || '—'],
                    ['Rack', fmp.rack_label || '—'],
                    ['WO #', fmp.wo_number || '—'],
                  ].map(([l, v]) => (
                    <div key={l} style={{ backgroundColor: '#0f172a', borderRadius: 6, padding: '8px 10px' }}>
                      <div style={{ fontSize: 10, color: '#475569', marginBottom: 3 }}>{l}</div>
                      <div style={{ fontSize: 13, color: '#94a3b8' }}>{v}</div>
                    </div>
                  ))}
                </div>

                {fmp.change_summary && (
                  <div style={{ backgroundColor: '#0f172a', borderRadius: 6, padding: '10px 12px', marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: '#475569', marginBottom: 4 }}>Change Summary</div>
                    <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.5 }}>{fmp.change_summary}</div>
                  </div>
                )}

                {/* Photo upload */}
                <PhotoUpload
                  contextType="fmp"
                  contextId={fmp.id}
                  photoType="rack_install"
                  label="Attach Photos"
                  canUpload={true}
                />

                {/* Acknowledgment stamp */}
                {fmp.acknowledged_by ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', backgroundColor: '#0d1f0d', border: '1px solid #166534', borderRadius: 8, marginTop: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#166534', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>✓</div>
                    <div>
                      <div style={{ color: '#4ade80', fontWeight: 'bold', fontSize: 13 }}>Accuracy Acknowledged</div>
                      <div style={{ color: '#64748b', fontSize: 11 }}>{fmp.acknowledged_by_name} · {new Date(fmp.acknowledged_at).toLocaleString()}</div>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => acknowledge(fmp.id)}
                    style={{ width: '100%', marginTop: 12, padding: '9px', backgroundColor: '#0f172a', border: '1px solid #22c55e44', color: '#22c55e', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>
                    ✓ Acknowledge Accuracy of this FMP
                  </button>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  {fmp.work_item_id && (
                    <button onClick={() => navigate(`/work/${fmp.work_item_id}`)}
                      style={{ padding: '8px 14px', backgroundColor: '#0f172a', border: '1px solid #334155', color: '#94a3b8', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
                      View Work Item
                    </button>
                  )}
                  <button onClick={() => verify(fmp.id)} disabled={verifying === fmp.id || !fmp.acknowledged_by}
                    title={!fmp.acknowledged_by ? 'Acknowledge accuracy first' : ''}
                    style={{ flex: 1, padding: '8px 20px', backgroundColor: verifying === fmp.id ? '#374151' : !fmp.acknowledged_by ? '#1e293b' : '#7c3aed', color: !fmp.acknowledged_by ? '#475569' : 'white', border: `1px solid ${!fmp.acknowledged_by ? '#334155' : 'transparent'}`, borderRadius: 7, cursor: !fmp.acknowledged_by ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 'bold' }}>
                    {verifying === fmp.id ? 'Sending…' : !fmp.acknowledged_by ? '🔒 Acknowledge First' : '→ Send to Design Queue'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Design Queue Tab ── */}
        {tab === 'design' && (
          <div>
            {designQueue.length === 0 ? (
              <div style={{ backgroundColor: '#1e293b', borderRadius: 12, padding: 48, textAlign: 'center', border: '1px solid #334155' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
                <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 18, marginBottom: 6 }}>Design queue is clear</div>
                <div style={{ color: '#64748b', fontSize: 14 }}>All FMPs have been reviewed and published.</div>
              </div>
            ) : designQueue.map(item => (
              <div key={item.id} style={{ backgroundColor: '#1e293b', borderRadius: 10, padding: 18, marginBottom: 12, border: '1px solid #ec489944', borderLeft: '4px solid #ec4899' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 15, marginBottom: 4 }}>
                      {item.work_item_title || `Rack Update — ${item.rack_label || item.location_name}`}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {item.sn_ticket && <span style={{ fontSize: 11, color: '#3b82f6', backgroundColor: '#1e3a5f', padding: '2px 7px', borderRadius: 4 }}>SN: {item.sn_ticket}</span>}
                      <span style={{ fontSize: 11, color: '#ec4899', backgroundColor: '#ec489922', padding: '2px 7px', borderRadius: 4 }}>{FMP_TYPE_LABELS[item.fmp_type] || item.fmp_type}</span>
                      {!item.work_item_id && <span style={{ fontSize: 11, color: '#f59e0b', backgroundColor: '#f59e0b22', padding: '2px 7px', borderRadius: 4 }}>Standalone</span>}
                      {item.no_change && <span style={{ fontSize: 11, color: '#94a3b8', backgroundColor: '#33415522', padding: '2px 7px', borderRadius: 4 }}>No Changes</span>}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', textAlign: 'right', flexShrink: 0 }}>
                    <div>Notified: {new Date(item.notified_at).toLocaleDateString()}</div>
                    <div>Verified by: {item.verified_by_name || '—'}</div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
                  {[
                    ['Site', item.location_name || '—'],
                    ['Rack', item.rack_label || '—'],
                    ['Generated By', item.generated_by_name || '—'],
                  ].map(([l, v]) => (
                    <div key={l} style={{ backgroundColor: '#0f172a', borderRadius: 6, padding: '8px 10px' }}>
                      <div style={{ fontSize: 10, color: '#475569', marginBottom: 3 }}>{l}</div>
                      <div style={{ fontSize: 13, color: '#94a3b8', textTransform: 'capitalize' }}>{v}</div>
                    </div>
                  ))}
                </div>

                {item.change_summary && !item.no_change && (
                  <div style={{ backgroundColor: '#0f172a', borderRadius: 6, padding: '10px 12px', marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: '#475569', marginBottom: 4 }}>Change Summary</div>
                    <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.5 }}>{item.change_summary}</div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8 }}>
                  {item.work_item_id && (
                    <button onClick={() => navigate(`/work/${item.work_item_id}`)}
                      style={{ padding: '8px 14px', backgroundColor: '#0f172a', border: '1px solid #334155', color: '#94a3b8', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
                      View Work Item
                    </button>
                  )}
                  <button onClick={() => publish(item.fmp_id)} disabled={publishing === item.fmp_id}
                    style={{ padding: '8px 20px', backgroundColor: publishing === item.fmp_id ? '#166534' : '#15803d', color: '#4ade80', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>
                    {publishing === item.fmp_id ? 'Publishing...' : '✓ Publish FMP'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
